import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import AuthService from '../services/AuthService';
import { requiresCheckIn } from '../utils/permissions';
import { broadcastAttendance, onAttendanceUpdate } from '../utils/attendanceBus';
import './CheckInGate.css';

/**
 * Blocks the entire admin portal behind a mandatory Check In prompt.
 *
 * Mounted once at the router root, so it fetches attendance status a single
 * time per page load rather than on every in-portal navigation (Header/
 * Sidebar/page content all remount per route, but this component doesn't).
 *
 * Only a front-desk/operational "Staff" account is gated (see
 * utils/permissions.requiresCheckIn) — the owner account and the built-in
 * "Admin" role never need to check in/out. Anything outside the authenticated
 * admin portal (login screen, public marketing pages) is exempt too. Once
 * checked in, Staff keep full access according to their assigned
 * permissions, same as before; checking out again (via the header widget) is
 * unaffected by this gate.
 */
const CheckInGate = ({ children }) => {
    const location = useLocation();
    const isAuthenticated = !!AuthService.getCurrentUser();
    const exempt =
        !isAuthenticated ||
        !requiresCheckIn() ||
        !location.pathname.startsWith('/admin') ||
        location.pathname === '/admin/login';

    const [state, setState] = useState(null);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await ApiService.request({ method: 'GET', url: 'admin/attendance/status' });
            if (res.data.status) setState(res.data.data);
        } catch (error) {
            // A failed status check leaves the gate closed until the next
            // successful fetch; ApiService already surfaced the error.
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        if (exempt || ready) return;
        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exempt, ready]);

    // Picks up a check-out made elsewhere (the header widget) so a session
    // closed there doesn't leave this gate's cached state stale.
    useEffect(() => {
        if (exempt) return;
        return onAttendanceUpdate(setState);
    }, [exempt]);

    const handleCheckIn = async () => {
        try {
            setLoading(true);
            const res = await ApiService.request({ method: 'POST', url: 'admin/attendance/check-in' });
            const data = res.data;
            setState(data.data);
            broadcastAttendance(data.data);
            data.status ? toast.success(data.message) : toast.error(data.message);
        } catch (error) {
            // Handled by ApiService.
        } finally {
            setLoading(false);
        }
    };

    if (exempt) return children;
    // Nothing renders until the first status call resolves, so the portal
    // never flashes open before the gate has a chance to close it.
    if (!ready) return null;

    // Fail closed: a status call that never came back (network hiccup) is
    // treated the same as "not checked in yet" rather than silently letting
    // the portal through.
    const mustCheckIn = !state || Boolean(state.can_check_in);
    if (!mustCheckIn) return children;

    // The dashboard still renders underneath, blurred and translucent — the
    // staff member can see they've landed in the portal, they just can't
    // reach it: the overlay covers the full viewport above everything else,
    // so every click lands on it, not on the page behind.
    return (
        <>
            {children}
            <div className="checkin-gate-overlay">
                <div className="checkin-gate-card">
                    <div className="checkin-gate-icon">
                        <i className="bi bi-box-arrow-in-right fs-1 text-base"></i>
                    </div>
                    <h3>Check In Required</h3>
                    <p>Start your work session to access the portal. You won't be able to view or do anything until you check in.</p>
                    <button
                        type="button"
                        className="btn btn-base checkin-gate-btn"
                        onClick={handleCheckIn}
                        disabled={loading}
                    >
                        {loading ? 'Checking in…' : 'Check In'}
                    </button>
                    <button type="button" className="checkin-gate-logout" onClick={() => AuthService.logout()}>
                        Not you? Log out
                    </button>
                </div>
            </div>
        </>
    );
};

export default CheckInGate;
